import { Injectable, PLATFORM_ID, REQUEST, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CatalogSortKey } from './catalog.models';
import { readCookie, serializeCookie } from '../../../shared/utils/cookie.util';
import { environment } from '../../../../environments/environment';

export type CatalogViewMode = 'grid' | 'list';
export type AvailabilityValue = '' | 'in_stock' | 'out_of_stock';

export interface CatalogPreferences {
  viewMode?: CatalogViewMode;
  sortKey?: CatalogSortKey;
  perPage?: number;
  availability?: AvailabilityValue;
}

// Persistimos en COOKIE (no localStorage) para que el render SSR pueda leer las
// mismas preferencias que el cliente: así server y cliente resuelven los mismos
// `CatalogParams` y la clave de TransferState de `CatalogService.getCatalogData`
// hace match, evitando el re-fetch/flash al entrar directo por URL. Ver el
// comentario en `catalog-page.component.ts` (bloque del `read()`).
const COOKIE_NAME = 'perron.catalog.prefs.v1';
const MAX_AGE_SECONDS = 60 * 60 * 24 * 365; // 1 año

const VALID_VIEW_MODES: ReadonlySet<CatalogViewMode> = new Set(['grid', 'list']);
const VALID_SORT_KEYS: ReadonlySet<CatalogSortKey> = new Set([
  'newest', 'price-asc', 'price-desc', 'name-asc', 'name-desc', 'popular',
]);
const VALID_AVAILABILITY: ReadonlySet<AvailabilityValue> = new Set(['', 'in_stock', 'out_of_stock']);

@Injectable({ providedIn: 'root' })
export class CatalogPreferencesService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  // En SSR con RenderMode.Server, `REQUEST` es el `Request` web entrante (con la
  // cookie). En browser/prerender es `null` (por eso `optional`).
  private readonly request = inject(REQUEST, { optional: true });

  read(): CatalogPreferences {
    try {
      const raw = readCookie(this.cookieHeader(), COOKIE_NAME);
      if (raw) return this.sanitize(JSON.parse(raw) as Partial<CatalogPreferences>);
      // Sin cookie: en browser intentamos migrar prefs legacy de localStorage.
      if (this.isBrowser) return this.migrateLegacy();
      return {};
    } catch {
      return {};
    }
  }

  write(patch: Partial<CatalogPreferences>): void {
    if (!this.isBrowser) return;
    try {
      const next = this.sanitize({ ...this.read(), ...patch });
      this.persist(next);
    } catch {
      /* privacy mode / cookies deshabilitadas — fail silently */
    }
  }

  /** Header de cookies según plataforma: `document.cookie` en browser, la request en SSR. */
  private cookieHeader(): string {
    if (this.isBrowser) {
      return typeof document !== 'undefined' ? document.cookie : '';
    }
    return this.request?.headers.get('cookie') ?? '';
  }

  /** Escribe la cookie (solo browser). `Secure` solo en prod (https). */
  private persist(prefs: CatalogPreferences): void {
    document.cookie = serializeCookie(COOKIE_NAME, JSON.stringify(prefs), {
      maxAgeSeconds: MAX_AGE_SECONDS,
      path: '/',
      sameSite: 'Lax',
      secure: environment.production,
    });
  }

  /**
   * Migración one-shot desde la versión localStorage: si un usuario tenía prefs
   * guardadas antes del cambio a cookie, las adopta, las reescribe como cookie y
   * limpia la clave legacy. Tras esto el servicio ya no vuelve a tocar localStorage.
   */
  private migrateLegacy(): CatalogPreferences {
    try {
      const legacy = localStorage.getItem(COOKIE_NAME);
      if (!legacy) return {};
      const prefs = this.sanitize(JSON.parse(legacy) as Partial<CatalogPreferences>);
      this.persist(prefs);
      localStorage.removeItem(COOKIE_NAME);
      return prefs;
    } catch {
      return {};
    }
  }

  private sanitize(p: Partial<CatalogPreferences>): CatalogPreferences {
    const out: CatalogPreferences = {};
    if (p.viewMode && VALID_VIEW_MODES.has(p.viewMode)) out.viewMode = p.viewMode;
    if (p.sortKey && VALID_SORT_KEYS.has(p.sortKey)) out.sortKey = p.sortKey;
    if (typeof p.perPage === 'number' && Number.isInteger(p.perPage) && p.perPage > 0 && p.perPage <= 500) {
      out.perPage = p.perPage;
    }
    if (p.availability !== undefined && VALID_AVAILABILITY.has(p.availability)) {
      out.availability = p.availability;
    }
    return out;
  }
}
